import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  runTransaction,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';

const KioskCheckoutPage = () => {
  const { kioskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [kiosk, setKiosk] = useState(null);
  const [loc, setLoc] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastConfirmationNumber, setLastConfirmationNumber] = useState('');

  // Cart from navigation state (mutable)
  const initialCart = location.state?.cart || [];
  const [cart, setCart] = useState(initialCart);

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  // Payment method selection
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  // Terminal payment state
  const [showTerminalWaitingModal, setShowTerminalWaitingModal] = useState(false);
  const [terminalStatusMessage, setTerminalStatusMessage] = useState('');
  const stripePollIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const hasFinalizedRef = useRef(false);

  // Inactivity timer: clear cart and return to kiosk after configured checkout timeout minutes of no interaction
  useEffect(() => {
    const ACTIVITY_EVENTS = ['click', 'touchstart', 'keydown', 'scroll', 'mousemove'];
    const CHECKOUT_MS = (kiosk?.checkoutTimeoutMinutes || 2) * 60 * 1000;
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, updateActivity, { passive: true }));

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= CHECKOUT_MS) {
        setCart([]);
        navigate(`/admin/kiosk/${kioskId}`);
      }
    }, 10000);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, updateActivity));
      clearInterval(interval);
    };
  }, [kioskId, navigate, kiosk?.checkoutTimeoutMinutes]);

  const handleContinueShopping = () => {
    navigate(`/admin/kiosk/${kioskId}`, { state: { cart } });
  };

  // Tip state (same pattern as PosPage)
  const [tipIsPercent, setTipIsPercent] = useState(true);
  const [tipInput, setTipInput] = useState('');
  const [skipTipping, setSkipTipping] = useState(false);

  const cartCount = cart.reduce((sum, item) => sum + (item.qty || 0), 0);
  const cartSubtotal = cart.reduce((sum, item) => sum + (item.total || 0), 0);
  const taxRate = loc?.taxRate || 0;
  const cartTax = cartSubtotal * (taxRate / 100);
  const calculatedTip = tipIsPercent
    ? cartSubtotal * ((parseFloat(tipInput) || 0) / 100)
    : (parseFloat(tipInput) || 0);
  const cartTotal = cartSubtotal + cartTax + calculatedTip;

  // Convenience fee (preview parity with kiosk-machine): CARD only, from the
  // location config. The backend (processTerminalPayment) re-computes and adds
  // it server-side on top of the pre-fee `amount` — we show the same number so
  // the customer sees exactly what the card will be charged (disclosure).
  const convenienceFeeCfg = loc?.convenienceFee;
  const convenienceFeeAmount = (selectedPaymentMethod === 'card' && convenienceFeeCfg?.enabled === true)
    ? cartTotal * ((parseFloat(convenienceFeeCfg.percent) || 0) / 100)
    : 0;
  const cartTotalWithFee = cartTotal + convenienceFeeAmount;

  // Auto-select payment method when kiosk loads
  useEffect(() => {
    if (kiosk) {
      const hasTerminal = !!kiosk.stripeTerminalId;
      // Same rule as the render path: location channel AND device toggle
      const byChannel = loc?.acceptCashByChannel;
      const channelVal = byChannel?.kiosk;
      const channelAllows = typeof channelVal === 'boolean' ? channelVal : loc?.acceptCash !== false;
      const hasCash = channelAllows && kiosk.acceptsCash === true;
      if (hasTerminal && !hasCash) {
        setSelectedPaymentMethod('card');
      } else if (!hasTerminal && hasCash) {
        setSelectedPaymentMethod('cash');
      }
    }
  }, [kiosk, loc]);

  // Auto-set skipTipping when a tip is entered in the app
  useEffect(() => {
    setSkipTipping(calculatedTip > 0);
  }, [calculatedTip]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (stripePollIntervalRef.current) {
        clearInterval(stripePollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  // Auto-return to menu after 5 seconds on success screen
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        startNewOrder();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const loadData = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();

      // Load kiosk
      const kioskDoc = await getDoc(doc(db, 'users', uid, 'vkiosks', kioskId));
      if (!kioskDoc.exists()) {
        Swal.fire('Error', 'Kiosk not found', 'error');
        navigate('/admin/vkiosks');
        return;
      }
      const kioskData = { id: kioskDoc.id, ...kioskDoc.data() };
      setKiosk(kioskData);

      // Load location
      if (kioskData.locationId) {
        const locDoc = await getDoc(doc(db, 'users', uid, 'locations', kioskData.locationId));
        if (locDoc.exists()) {
          setLoc({ id: locDoc.id, ...locDoc.data() });
        }
      }

      // Determine session id
      if (location.state?.sessionId) {
        setSessionId(location.state.sessionId);
      }
    } catch (e) {
      console.error('Error loading checkout data:', e);
    } finally {
      setLoading(false);
    }
  };

  const validateCustomerInfo = () => {
    if (!customerName.trim() && !customerEmail.trim() && !customerPhone.trim()) {
      Swal.fire('Error', 'Please enter at least one customer detail (name, email, or phone)', 'error');
      return false;
    }

    if (customerEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerEmail.trim())) {
        Swal.fire('Error', 'Please enter a valid email address', 'error');
        return false;
      }
    }

    if (customerPhone.trim()) {
      const digitsOnly = customerPhone.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        Swal.fire('Error', 'Please enter a valid phone number (at least 10 digits)', 'error');
        return false;
      }
    }

    return true;
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    const len = digits.length;
    if (len === 0) return '';
    if (len < 4) return `(${digits}`;
    if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const updateQty = (idx, delta) => {
    setCart(prev => {
      const updated = [...prev];
      const item = { ...updated[idx] };
      const newQty = Math.max(1, (item.qty || 1) + delta);
      item.qty = newQty;
      item.total = (item.price || 0) * newQty;
      updated[idx] = item;
      return updated;
    });
  };

  const removeItem = (idx) => {
    setCart(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      if (updated.length === 0) {
        navigate(`/admin/kiosk/${kioskId}`);
      }
      return updated;
    });
  };

  const handleCancelOrder = () => {
    Swal.fire({
      title: 'Cancel Order?',
      text: 'Are you sure you want to cancel this order?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'No, keep it',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(`/admin/kiosk/${kioskId}`);
      }
    });
  };

  const startTerminalPayment = async () => {
    if (!user || !kiosk || !sessionId || !loc) return;
    if (!validateCustomerInfo()) return;

    setSaving(true);
    try {
      const functions = getFunctions();
      const processTerminalPaymentFn = httpsCallable(functions, 'processTerminalPayment');
      const timestamp = new Date();
      const confirmationNumber = `KIOSK-${timestamp.getTime().toString().slice(-6)}`;

      const result = await processTerminalPaymentFn({
        amount: cartTotal,
        currency: 'usd',
        stripeTerminalId: kiosk.stripeTerminalId,
        stripeLocationId: loc?.stripeLocationId,
        locationId: kiosk.locationId,
        amountEligible: cartSubtotal,
        skipTipping: skipTipping,
        description: `Kiosk Order #${confirmationNumber}`,
        metadata: {
          kioskId: kiosk.id,
          kioskSessionId: sessionId,
          userId: user.uid,
          locationId: kiosk.locationId,
        },
        cartItems: cart.map(item => ({
          productName: item.productName,
          qty: item.qty,
          total: item.total,
          price: item.price,
        })),
        cartTax: cartTax,
        cartTotal: cartTotal,
      });

      const { paymentIntentId } = result.data;
      setTerminalStatusMessage('Please tap or insert your card on the terminal...');
      setShowTerminalWaitingModal(true);

      pollTerminalPaymentStatus(paymentIntentId, confirmationNumber);
    } catch (error) {
      console.error('Error starting terminal payment:', error);
      Swal.fire('Error', error.message || 'Failed to start terminal payment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const pollTerminalPaymentStatus = (paymentIntentId, confirmationNumber) => {
    if (stripePollIntervalRef.current) {
      clearInterval(stripePollIntervalRef.current);
    }

    let pollCount = 0;
    const maxPolls = 120; // ~6 minutes at 3 second intervals
    let hasSucceeded = false;

    const poll = async () => {
      pollCount++;
      if (pollCount > maxPolls) {
        clearInterval(stripePollIntervalRef.current);
        setShowTerminalWaitingModal(false);
        Swal.fire('Timeout', 'Terminal payment timed out. Please try again.', 'warning');
        return;
      }

      // Prevent duplicate finalization if interval fires while a previous poll is still finalizing
      if (hasSucceeded) return;

      try {
        const functions = getFunctions();
        const checkTerminalPaymentStatusFn = httpsCallable(functions, 'checkTerminalPaymentStatus');
        const result = await checkTerminalPaymentStatusFn({ paymentIntentId });
        const { status, chargeAmount, tipAmount, charges, lastPaymentError } = result.data;

        if (status === 'succeeded') {
          hasSucceeded = true;
          clearInterval(stripePollIntervalRef.current);
          setShowTerminalWaitingModal(false);

          const finalTotal = (chargeAmount || 0) / 100;
          const finalTip = (tipAmount || 0) / 100;
          // If terminal reported no tip but tip was already added in the app,
          // use the app's tip so the receipt shows the correct amount.
          const effectiveTip = finalTip || calculatedTip;

          await finalizeKioskSale({
            confirmationNumber,
            paymentMethod: 'card',
            paymentType: 'terminal',
            stripePaymentIntentId: paymentIntentId,
            cardBrand: charges?.[0]?.cardBrand,
            cardLast4: charges?.[0]?.cardLast4,
            tipOverride: effectiveTip,
            totalOverride: finalTotal,
          });
        } else if (status === 'canceled') {
          clearInterval(stripePollIntervalRef.current);
          setShowTerminalWaitingModal(false);
          Swal.fire('Payment Failed', 'The card payment was not completed on the terminal.', 'error');
        } else if (status === 'requires_payment_method') {
          if (lastPaymentError) {
            setTerminalStatusMessage(`Card declined: ${lastPaymentError.message || 'Please try a different card.'}`);
          } else {
            setTerminalStatusMessage('Please tap or insert your card on the terminal...');
          }
        } else if (status === 'processing') {
          setTerminalStatusMessage('Processing payment on terminal...');
        } else if (status === 'requires_capture') {
          setTerminalStatusMessage('Payment authorized. Finalizing...');
        } else if (status === 'requires_action') {
          setTerminalStatusMessage('Additional authentication required on terminal...');
        }
      } catch (error) {
        console.error('Error polling terminal payment:', error);
      }
    };

    poll();
    stripePollIntervalRef.current = setInterval(poll, 3000);
  };

  const cancelTerminalPayment = async () => {
    if (stripePollIntervalRef.current) {
      clearInterval(stripePollIntervalRef.current);
    }
    if (kiosk?.stripeTerminalId) {
      try {
        const functions = getFunctions();
        const cancelTerminalActionFn = httpsCallable(functions, 'cancelTerminalAction');
        await cancelTerminalActionFn({ stripeTerminalId: kiosk.stripeTerminalId });
      } catch (error) {
        console.error('Error canceling terminal action:', error);
      }
    }
    setShowTerminalWaitingModal(false);
    setTerminalStatusMessage('');
  };

  const finalizeKioskSale = async (overrides = {}) => {
    if (hasFinalizedRef.current) {
      console.log('[KioskCheckout] finalizeKioskSale already executed, skipping duplicate');
      return;
    }
    hasFinalizedRef.current = true;

    if (!user || !kiosk || !sessionId || !loc) return;

    setSaving(true);
    try {
      const db = getFirestore();
      const timestamp = new Date();
      const saleId = doc(collection(db, 'users', user.uid, 'sales')).id;
      const confirmationNumber = overrides.confirmationNumber || `KIOSK-${timestamp.getTime().toString().slice(-6)}`;

      // Calculate guest number
      let guestNumber = null;
      if (kiosk?.locationId && user?.uid) {
        const locRef = doc(db, 'users', user.uid, 'locations', kiosk.locationId);
        try {
          guestNumber = await runTransaction(db, async (transaction) => {
            const locDoc = await transaction.get(locRef);
            let ordersPosition = locDoc.data()?.ordersPosition || 0;
            const ordersCorrelative = locDoc.data()?.ordersCorrelative || 1;
            ordersPosition++;
            if (ordersPosition >= ordersCorrelative * 100) ordersPosition = 1;
            transaction.update(locRef, { ordersPosition });
            return ordersPosition.toString().padStart(4, '0');
          });
        } catch (e) {
          console.error('Error generating guest number:', e);
        }
      }

      const saleItems = cart.map((item) => ({
        productId: item.productId,
        productName: item.productName,
        productDescription: item.productDescription || '',
        productImage: item.productImage || null,
        qty: item.qty || 0,
        price: item.price || 0,
        total: item.total || 0,
        options: item.options || [],
        instructions: item.instructions || null,
        categoryId: item.categoryId || null,
        categoryName: item.categoryName || '',
        workzoneId: item.workzoneId || null,
        date: item.date || timestamp,
        fulfillmentStatus: 'sent_to_kitchen',
      }));

      const finalTip = overrides.tipOverride !== undefined ? overrides.tipOverride : calculatedTip;
      const finalTotal = overrides.totalOverride !== undefined ? overrides.totalOverride : cartTotal;

      const saleData = {
        id: saleId,
        userId: user.uid,
        locationId: kiosk.locationId,
        locationName: loc?.companyName || loc?.locationName || '',
        kioskId: kiosk.id,
        kioskSessionId: sessionId,
        salesChannel: 'KIOSK',
        customerId: null,
        fodyesUid: null,
        customerName: customerName.trim() || 'Kiosk Customer',
        customerEmail: customerEmail.trim() || '',
        customerPhone: customerPhone.trim() || '',
        orderType: 'kiosk',
        isGuestCustomer: true,
        confirmationNumber,
        guestNumber,
        modifiedAt: timestamp,
        closeAt: timestamp,
        paymentType: overrides.paymentType || 'kiosk',
        paymentMethod: overrides.paymentMethod || 'kiosk',
        status: 'completed',
        paymentStatus: 'completed',
        orderStatus: 'completed',
        fulfillmentStatus: 'sent_to_kitchen',
        cardType: overrides.cardBrand || null,
        cardLast4: overrides.cardLast4 || null,
        stripePaymentIntentId: overrides.stripePaymentIntentId || null,
        itemsQty: cartCount,
        subtotal: cartSubtotal,
        tax: cartTax,
        tip: finalTip,
        tipPercent: tipIsPercent ? (parseFloat(tipInput) || 0) : 0,
        tipIsPercent,
        discount: 0,
        // The fee the customer paid for paying by card (0 on cash) — the Day
        // Close "fees collected" line reads this field on POS sales already.
        convenienceFee: (overrides.paymentMethod === 'card') ? convenienceFeeAmount : 0,
        total: finalTotal,
        deliveryAddress: null,
        deliveryCity: null,
        deliveryState: null,
        deliveryZipCode: null,
        deliveryCountry: null,
        deliveryLatitude: null,
        deliveryLongitude: null,
        notes: '',
        orderDate: timestamp,
        createdAt: timestamp,
        updatedAt: timestamp,
      };

      // Save to user sales
      await setDoc(doc(db, 'users', user.uid, 'sales', saleId), saleData);
      await Promise.all(
        saleItems.map((item) =>
          setDoc(doc(collection(db, 'users', user.uid, 'sales', saleId, 'items')), item)
        )
      );

      // Save to location sales
      if (kiosk.locationId) {
        await setDoc(doc(db, 'users', user.uid, 'locations', kiosk.locationId, 'sales', saleId), saleData);
        await Promise.all(
          saleItems.map((item) =>
            setDoc(doc(collection(db, 'users', user.uid, 'locations', kiosk.locationId, 'sales', saleId, 'items')), item)
          )
        );
      }

      // Save to kiosk session sales
      await setDoc(doc(db, 'users', user.uid, 'kioskSessions', sessionId, 'sales', saleId), saleData);

      // Update session totals
      const sessionRef = doc(db, 'users', user.uid, 'kioskSessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      if (sessionDoc.exists()) {
        const currentData = sessionDoc.data();
        const updatePayload = {
          totalSales: (currentData.totalSales || 0) + finalTotal,
          totalTransactions: (currentData.totalTransactions || 0) + 1,
          updatedAt: timestamp,
        };
        if (overrides.paymentMethod === 'cash') {
          updatePayload.cash = (currentData.cash || 0) + finalTotal;
        } else if (overrides.paymentMethod === 'card') {
          updatePayload.cardSales = (currentData.cardSales || 0) + finalTotal;
        }
        await updateDoc(sessionRef, updatePayload);
      }

      setLastConfirmationNumber(confirmationNumber);
      setShowSuccess(true);

      // Auto-print receipt
      autoPrintReceipt(saleData, saleItems, confirmationNumber);
    } catch (e) {
      console.error('Error finalizing kiosk sale:', e);
      Swal.fire('Error', 'Failed to complete order', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePlaceCashOrder = async () => {
    if (!user || !kiosk || !sessionId || !loc) return;
    if (cart.length === 0) return;
    if (!validateCustomerInfo()) return;
    if (saving) return;

    setSaving(true);
    const timestamp = new Date();
    const confirmationNumber = `KIOSK-${timestamp.getTime().toString().slice(-6)}`;
    await finalizeKioskSale({
      confirmationNumber,
      paymentMethod: 'cash',
      paymentType: 'cash',
    });
  };

  const handlePlaceOrder = async () => {
    if (selectedPaymentMethod === 'card') {
      await startTerminalPayment();
    } else if (selectedPaymentMethod === 'cash') {
      await handlePlaceCashOrder();
    } else {
      Swal.fire('Error', 'Please select a payment method', 'error');
    }
  };

  const autoPrintReceipt = (saleData, saleItems, confirmationNumber) => {
    const printWindow = window.open('', '_blank', 'width=320,height=600');
    if (!printWindow) {
      console.warn('Popup blocked — receipt could not auto-print');
      return;
    }

    const timestamp = saleData.orderDate || new Date();
    const dateStr = timestamp.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    const itemsHtml = saleItems
      .map((item) => {
        const optionsStr =
          item.options?.length > 0
            ? `<div style="font-size:10px;color:#555;padding-left:8px;">+ ${item.options
                .map((o) => (typeof o === 'string' ? o : o.itemName || o.name))
                .join(', ')}</div>`
            : '';
        const instructionsStr = item.instructions
          ? `<div style="font-size:10px;color:#777;padding-left:8px;"><i>Note: ${item.instructions}</i></div>`
          : '';
        return `
          <div style="margin-bottom:6px;">
            <div style="display:flex;justify-content:space-between;align-items:flex-start;">
              <div style="flex:1;">
                <span style="font-weight:bold;">${item.qty}x</span> ${item.productName}
              </div>
              <div style="text-align:right;min-width:60px;">${formatCurrency(item.total)}</div>
            </div>
            ${optionsStr}
            ${instructionsStr}
          </div>
        `;
      })
      .join('');

    const customerHtml = `
      <div class="bold">Customer</div>
      <div>${saleData.customerName || 'Kiosk Customer'}</div>
      ${saleData.customerEmail ? `<div>${saleData.customerEmail}</div>` : ''}
      ${saleData.customerPhone ? `<div>${saleData.customerPhone}</div>` : ''}
    `;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Receipt #${confirmationNumber}</title>
          <style>
            @media print { body { margin: 0; } .no-print { display: none; } }
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: 12px; line-height: 1.4; color: #000;
              width: 80mm; max-width: 80mm; margin: 0 auto;
              padding: 8px; box-sizing: border-box;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .divider { border-top: 1px dashed #000; margin: 8px 0; }
            .line { display: flex; justify-content: space-between; }
            .total { font-size: 14px; font-weight: bold; }
            .logo { max-width: 60mm; max-height: 50px; object-fit: contain; margin-bottom: 4px; }
          </style>
        </head>
        <body>
          ${loc?.logoUrl ? `<div class="center"><img src="${loc.logoUrl}" class="logo" onerror="this.style.display='none'" /></div>` : ''}
          <div class="center bold" style="font-size:14px;">${loc?.companyName || loc?.locationName || 'Restaurant'}</div>
          ${loc?.address ? `<div class="center">${loc.address}</div>` : ''}
          ${loc?.city || loc?.state || loc?.zip ? `<div class="center">${[loc?.city, loc?.state, loc?.zip].filter(Boolean).join(', ')}</div>` : ''}
          ${loc?.phone ? `<div class="center">Tel: ${loc.phone}</div>` : ''}
          <div class="divider"></div>
          <div class="center bold">RECEIPT</div>
          <div class="line"><span>Order #:</span><span>${confirmationNumber}</span></div>
          <div class="line"><span>Date:</span><span>${dateStr}</span></div>
          <div class="line"><span>Type:</span><span>KIOSK</span></div>
          <div class="line"><span>Payment:</span><span>${saleData.paymentMethod === 'cash' ? 'CASH' : 'CARD'}</span></div>
          <div class="divider"></div>
          ${customerHtml}
          <div class="divider"></div>
          <div class="bold">Items</div>
          ${itemsHtml}
          <div class="divider"></div>
          <div class="line"><span>Subtotal</span><span>${formatCurrency(saleData.subtotal)}</span></div>
          <div class="line"><span>Tax (${loc?.taxRate || 0}%)</span><span>${formatCurrency(saleData.tax)}</span></div>
          ${saleData.tip > 0 ? `<div class="line"><span>Tip</span><span>${formatCurrency(saleData.tip)}</span></div>` : ''}
          <div class="divider"></div>
          <div class="line total"><span>TOTAL</span><span>${formatCurrency(saleData.total)}</span></div>
          <div class="divider"></div>
          <div class="center" style="font-size:11px;margin-top:4px;">
            <div>Thank you for your visit!</div>
            <div>Please come again</div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 300);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const startNewOrder = () => {
    navigate(`/admin/kiosk/${kioskId}`);
  };

  const hasTerminal = !!kiosk?.stripeTerminalId;
  // Cash at the kiosk requires BOTH: the location-level 'kiosk' sales
  // channel (acceptCashByChannel, legacy acceptCash as fallback) AND the
  // per-kiosk device toggle (acceptsCash).
  const kioskChannelAllowsCash = (() => {
    const byChannel = loc?.acceptCashByChannel;
    const channelVal = byChannel?.kiosk;
    return typeof channelVal === 'boolean' ? channelVal : loc?.acceptCash !== false;
  })();
  const hasCash = kioskChannelAllowsCash && kiosk?.acceptsCash === true;

  if (loading) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <img src="/fodyfee_shining.gif" alt="Loading..." style={{ width: 120, height: 120, objectFit: 'contain' }} />
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#ff6b35', color: '#fff', padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 96, marginBottom: 24 }}>
          <i className="fas fa-check-circle"></i>
        </div>
        <h1 style={{ fontSize: 48, fontWeight: 800, marginBottom: 16 }}>Order Placed!</h1>
        <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.9 }}>Your order number is</div>
        <div style={{ fontSize: 72, fontWeight: 900, letterSpacing: 4, marginBottom: 40, background: '#fff', color: '#ff6b35', padding: '16px 48px', borderRadius: 16 }}>
          {lastConfirmationNumber}
        </div>
        <button onClick={startNewOrder} style={{ fontSize: 28, fontWeight: 700, padding: '20px 64px', borderRadius: 16, border: 'none', background: '#fff', color: '#ff6b35', cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
          Start New Order
        </button>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: 40 }}>
        <i className="fas fa-shopping-basket" style={{ fontSize: 80, color: '#ccc', marginBottom: 24 }}></i>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: '#1a1a1a', marginBottom: 16 }}>Your cart is empty</h1>
        <button onClick={handleContinueShopping} style={{ fontSize: 20, fontWeight: 700, padding: '16px 48px', borderRadius: 16, border: 'none', background: '#ff6b35', color: '#fff', cursor: 'pointer' }}>
          Back to Menu
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5', overflow: 'hidden', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: '#fff', borderBottom: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={handleContinueShopping} style={{ width: 48, height: 48, borderRadius: 12, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: 20, color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-arrow-left"></i>
          </button>
          {loc?.logoUrl ? (
            <img src={loc.logoUrl} alt="Logo" style={{ height: 44, objectFit: 'contain', borderRadius: 8 }} onError={(e) => { e.target.style.display = 'none'; }} />
          ) : null}
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1a1a1a' }}>{loc?.companyName || loc?.locationName || 'Kiosk'}</div>
            <div style={{ fontSize: 14, color: '#888' }}>{kiosk?.kioskName || kioskId}</div>
          </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a', background: '#f0f0f0', padding: '8px 20px', borderRadius: 12, fontVariantNumeric: 'tabular-nums' }}>
          {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 40, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 720 }}>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: '#1a1a1a', marginBottom: 32 }}>Review Your Order</h1>

          {/* Items */}
          <div style={{ background: '#fff', borderRadius: 20, padding: 24, marginBottom: 24, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginBottom: 16 }}>Items ({cartCount})</div>
            {cart.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0', borderBottom: idx < cart.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                {item.productImage ? (
                  <img src={item.productImage} alt="" style={{ width: 64, height: 64, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 64, height: 64, borderRadius: 12, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="fas fa-utensils" style={{ color: '#ccc' }}></i>
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>
                    {item.productName}
                  </div>
                  {item.options?.length > 0 && (
                    <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>
                      + {item.options.map((o) => (typeof o === 'string' ? o : o.itemName || o.name)).join(', ')}
                    </div>
                  )}
                  {item.instructions && (
                    <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}><i>Note: {item.instructions}</i></div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={() => updateQty(idx, -1)}
                    disabled={item.qty <= 1}
                    style={{
                      width: 32, height: 32, borderRadius: 8, border: '1px solid #e0e0e0',
                      background: '#fff', cursor: item.qty <= 1 ? 'not-allowed' : 'pointer',
                      fontSize: 16, color: item.qty <= 1 ? '#ccc' : '#ff6b35', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', minWidth: 28, textAlign: 'center' }}>{item.qty}</span>
                  <button
                    onClick={() => updateQty(idx, 1)}
                    style={{
                      width: 32, height: 32, borderRadius: 8, border: '1px solid #e0e0e0',
                      background: '#fff', cursor: 'pointer',
                      fontSize: 16, color: '#ff6b35', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                  <button
                    onClick={() => removeItem(idx)}
                    style={{
                      width: 32, height: 32, borderRadius: 8, border: '1px solid #dc3545',
                      background: '#fff', cursor: 'pointer',
                      fontSize: 14, color: '#dc3545', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', marginLeft: 8,
                    }}
                    title="Remove item"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#ff6b35', minWidth: 70, textAlign: 'right' }}>{formatCurrency(item.total)}</div>
              </div>
            ))}
          </div>

          {/* Customer Info */}
          <div style={{ background: '#fff', borderRadius: 20, padding: 24, marginBottom: 24, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginBottom: 16 }}>Customer Information</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Name (required if no email/phone)"
                style={{ padding: '14px 16px', borderRadius: 12, border: '1px solid #e0e0e0', fontSize: 18, fontWeight: 600 }}
              />
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="Email (required if no name/phone)"
                style={{ padding: '14px 16px', borderRadius: 12, border: '1px solid #e0e0e0', fontSize: 18, fontWeight: 600 }}
              />
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(formatPhone(e.target.value))}
                placeholder="Phone (required if no name/email)"
                style={{ padding: '14px 16px', borderRadius: 12, border: '1px solid #e0e0e0', fontSize: 18, fontWeight: 600 }}
              />
            </div>
          </div>

          {/* Tip Section */}
          <div style={{ background: '#fff', borderRadius: 20, padding: 24, marginBottom: 24, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}>Tip</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#ff6b35' }}>{formatCurrency(calculatedTip)}</div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {[10, 15, 20, 25].map((pct) => (
                <button
                  key={pct}
                  onClick={() => { setTipIsPercent(true); setTipInput(pct.toString()); }}
                  style={{
                    flex: 1,
                    padding: '14px 0',
                    borderRadius: 16,
                    border: 'none',
                    fontSize: 18,
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: tipIsPercent && parseFloat(tipInput) === pct ? '#ff6b35' : '#f0f0f0',
                    color: tipIsPercent && parseFloat(tipInput) === pct ? '#fff' : '#555',
                    transition: 'all 0.2s',
                  }}
                >
                  {pct}%
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="number"
                value={tipInput}
                placeholder={tipIsPercent ? 'Custom %' : 'Custom $'}
                onChange={(e) => setTipInput(e.target.value)}
                style={{ flex: 1, padding: '14px 16px', borderRadius: 12, border: '1px solid #e0e0e0', fontSize: 18, fontWeight: 700 }}
              />
              <button
                onClick={() => { setTipIsPercent(true); }}
                style={{
                  padding: '14px 20px',
                  borderRadius: 12,
                  border: 'none',
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: tipIsPercent ? '#ff6b35' : '#f0f0f0',
                  color: tipIsPercent ? '#fff' : '#555',
                }}
              >
                %
              </button>
              <button
                onClick={() => { setTipIsPercent(false); setTipInput(''); }}
                style={{
                  padding: '14px 20px',
                  borderRadius: 12,
                  border: 'none',
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: !tipIsPercent ? '#ff6b35' : '#f0f0f0',
                  color: !tipIsPercent ? '#fff' : '#555',
                }}
              >
                $
              </button>
            </div>
          </div>

          {/* Totals */}
          <div style={{ background: '#fff', borderRadius: 20, padding: 24, marginBottom: 32, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 18, color: '#555' }}>
              <span>Subtotal ({cartCount} items)</span>
              <span style={{ fontWeight: 700, color: '#1a1a1a' }}>{formatCurrency(cartSubtotal)}</span>
            </div>
            {taxRate > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 18, color: '#555' }}>
                <span>Tax ({taxRate}%)</span>
                <span style={{ fontWeight: 700, color: '#1a1a1a' }}>{formatCurrency(cartTax)}</span>
              </div>
            )}
            {calculatedTip > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 18, color: '#555' }}>
                <span>Tip</span>
                <span style={{ fontWeight: 700, color: '#1a1a1a' }}>{formatCurrency(calculatedTip)}</span>
              </div>
            )}
            {convenienceFeeAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 18, color: '#555' }}>
                <span>Convenience Fee ({parseFloat(convenienceFeeCfg.percent) || 0}% — card payments)</span>
                <span style={{ fontWeight: 700, color: '#1a1a1a' }}>{formatCurrency(convenienceFeeAmount)}</span>
              </div>
            )}
            <div style={{ borderTop: '2px solid #f0f0f0', marginTop: 16, paddingTop: 16, display: 'flex', justifyContent: 'space-between', fontSize: 32, fontWeight: 800, color: '#1a1a1a' }}>
              <span>Total</span>
              <span style={{ color: '#ff6b35' }}>{formatCurrency(cartTotalWithFee)}</span>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div style={{ background: '#fff', borderRadius: 20, padding: 24, marginBottom: 32, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginBottom: 16 }}>Payment Method</div>

            {hasTerminal && (
              <button
                onClick={() => setSelectedPaymentMethod('card')}
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '18px 0',
                  borderRadius: 16,
                  border: selectedPaymentMethod === 'card' ? '2px solid #ff6b35' : '2px solid #e0e0e0',
                  background: selectedPaymentMethod === 'card' ? '#fff3ee' : '#fff',
                  color: selectedPaymentMethod === 'card' ? '#ff6b35' : '#555',
                  fontSize: 20,
                  fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                }}
              >
                <i className="fas fa-credit-card"></i> Pay with Card
              </button>
            )}

            {hasCash && (
              <button
                onClick={() => setSelectedPaymentMethod('cash')}
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '18px 0',
                  borderRadius: 16,
                  border: selectedPaymentMethod === 'cash' ? '2px solid #ff6b35' : '2px solid #e0e0e0',
                  background: selectedPaymentMethod === 'cash' ? '#fff3ee' : '#fff',
                  color: selectedPaymentMethod === 'cash' ? '#ff6b35' : '#555',
                  fontSize: 20,
                  fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                }}
              >
                <i className="fas fa-money-bill-wave"></i> Pay with Cash
              </button>
            )}

            {!hasTerminal && !hasCash && (
              <div style={{ color: '#dc3545', fontSize: 16, fontWeight: 700, textAlign: 'center', padding: '16px', background: '#f8d7da', borderRadius: 12 }}>
                <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }}></i>
                No payment method available. Please contact staff.
              </div>
            )}
          </div>

          {/* Place Order */}
          <button
            onClick={handlePlaceOrder}
            disabled={saving || (!hasTerminal && !hasCash)}
            style={{
              width: '100%',
              padding: '24px 0',
              borderRadius: 20,
              border: 'none',
              background: saving || (!hasTerminal && !hasCash) ? '#ccc' : '#ff6b35',
              color: '#fff',
              fontSize: 28,
              fontWeight: 800,
              cursor: saving || (!hasTerminal && !hasCash) ? 'not-allowed' : 'pointer',
              boxShadow: saving || (!hasTerminal && !hasCash) ? 'none' : '0 8px 24px rgba(255,107,53,0.3)',
            }}
          >
            {saving ? (
              <span><i className="fas fa-spinner fa-spin" style={{ marginRight: 12 }}></i>Processing...</span>
            ) : (
              <span>
                <i className="fas fa-check-circle" style={{ marginRight: 12 }}></i>
                {selectedPaymentMethod === 'card' && `Pay with Card — ${formatCurrency(cartTotal)}`}
                {selectedPaymentMethod === 'cash' && `Pay with Cash — ${formatCurrency(cartTotal)}`}
                {!selectedPaymentMethod && 'Select Payment Method'}
              </span>
            )}
          </button>

          {/* Continue Shopping */}
          <button
            onClick={handleContinueShopping}
            disabled={saving}
            style={{
              width: '100%',
              padding: '18px 0',
              borderRadius: 20,
              border: '2px solid #e0e0e0',
              background: '#fff',
              color: '#555',
              fontSize: 20,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              marginTop: 16,
            }}
          >
            <i className="fas fa-arrow-left" style={{ marginRight: 10 }}></i>Continue Shopping
          </button>

          {/* Cancel Order */}
          <button
            onClick={handleCancelOrder}
            disabled={saving}
            style={{
              width: '100%',
              padding: '18px 0',
              borderRadius: 20,
              border: '2px solid #dc3545',
              background: '#fff',
              color: '#dc3545',
              fontSize: 20,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              marginTop: 16,
            }}
          >
            <i className="fas fa-times" style={{ marginRight: 10 }}></i>Cancel Order
          </button>
        </div>
      </div>

      {/* Terminal Waiting Modal */}
      {showTerminalWaitingModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          zIndex: 5000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: '48px 40px',
            maxWidth: 480,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: 64, color: '#ff6b35', marginBottom: 24 }}>
              <i className="fas fa-credit-card"></i>
            </div>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a1a', marginBottom: 16 }}>Terminal Payment</h2>
            <p style={{ fontSize: 18, color: '#555', marginBottom: 32, lineHeight: 1.5 }}>
              {terminalStatusMessage}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 32 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff6b35', animation: 'pulse 1.5s infinite' }}></div>
              <span style={{ fontSize: 16, color: '#888', fontWeight: 600 }}>Waiting for terminal...</span>
            </div>
            <button
              onClick={cancelTerminalPayment}
              style={{
                padding: '14px 32px',
                borderRadius: 12,
                border: '2px solid #dc3545',
                background: '#fff',
                color: '#dc3545',
                fontSize: 18,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Cancel Payment
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default KioskCheckoutPage;
